// src/pages/TokenDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
// import { useWeb3 } from '../contexts/Web3Context';
import axios from 'axios';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  ArrowLeftIcon,
  UserCircleIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import LoadingSpinner from '../components/LoadingSpinner';
import ProposalCard from '../components/ProposalCard';

const TokenDetail = () => {
  const { address } = useParams();
  // const { account } = useWeb3();

  const [token, setToken] = useState(null);
  const [tokenHolders, setTokenHolders] = useState([]);
  const [tokenProposals, setTokenProposals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // const [tokenImage, setTokenImage] = useState(null);

  // 色の配列
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

  // トークン情報を取得
  useEffect(() => {
    const fetchTokenData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // トークン情報を取得 - addressはgrantTokenとして扱う
        const tokenResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tokens/${address}`
        );
        setToken(tokenResponse.data);

        // ホルダー情報を取得
        const holdersResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tokens/${address}/holders`
        );
        setTokenHolders(holdersResponse.data);

        // トークンに関連する提案を取得
        const proposalsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/proposals`);
        // クライアント側でフィルタリング
        const filteredProposals = proposalsResponse.data.filter(
          proposal => proposal.token && proposal.token.grantToken === address
        );
        setTokenProposals(filteredProposals);

        setIsLoading(false);
      } catch (error) {
        console.error('トークンデータ取得エラー:', error);
        setError('トークン情報の取得に失敗しました');
        setIsLoading(false);
      }
    };

    if (address) {
      fetchTokenData();
    }
  }, [address]);

  // // トークン画像を取得
  // useEffect(() => {
  //   const fetchTokenImage = async () => {
  //     if (!token) return;

  //     // try {
  //     //   const imageUrl = `${process.env.REACT_APP_API_URL}/api/tokens/${address}/image`;
  //     //   setTokenImage(`${imageUrl}`);
  //     // } catch (error) {
  //     //   // 画像取得エラー時にtoken.imgを使用
  //     //   if (token.img) {
  //     //     setTokenImage(`${token.img}`);
  //     //   }
  //     //   console.log('Token image not found:', error);
  //     // }
  //   };

  //   if (token) {
  //     fetchTokenImage();
  //   }
  // }, [token, address]);

  // トークンアイコンの背景色を生成
  const getTokenColor = () => {
    if (!token) return 'bg-blue-100 dark:bg-blue-800';

    // アドレスの取得（grantToken）
    const tokenAddress = token.grantToken;
    if (!tokenAddress) return 'bg-blue-100 dark:bg-blue-800';

    // アドレスの末尾6文字を取得してハッシュ化
    const hash = tokenAddress.substring(tokenAddress.length - 6);
    const hue = parseInt(hash, 16) % 360;

    // 明るめのパステルカラーを返す
    return `hsl(${hue}, 70%, 85%)`;
  };

  // ブロックエクスプローラーのリンクを生成
  const getExplorerLink = addr => {
    return `${process.env.REACT_APP_BLOCK_EXPLORER_URL}/address/${addr}`;
  };

  // アドレスをクリップボードにコピー
  const copyToClipboard = text => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      err => {
        console.error('クリップボードへのコピーに失敗しました:', err);
      }
    );
  };

  // トークンシンボルから最初の2文字を取得
  const getTokenInitials = () => {
    if (!token || !token.symbol) return '??';
    return token.symbol.substring(0, 2).toUpperCase();
  };

  // 上位ホルダーとその他を取得（円グラフ用）
  const getTopHolders = () => {
    if (!tokenHolders || tokenHolders.length === 0) return [];

    try {
      // 残高でソート - BigInt 対応の比較を使用
      const sortedHolders = [...tokenHolders].sort((a, b) => {
        try {
          // 両方の残高をBigIntに変換
          const balanceA = BigInt(a.balance || 0);
          const balanceB = BigInt(b.balance || 0);

          // BigInt 同士の比較結果から -1, 0, 1 を返す
          if (balanceA > balanceB) return -1;
          if (balanceA < balanceB) return 1;
          return 0;
        } catch (err) {
          console.error('Error comparing token balances:', err);
          return 0;
        }
      });

      // 上位5件とその他
      const topHolders = sortedHolders.slice(0, 5);

      // 残りをその他としてまとめる
      if (sortedHolders.length > 5) {
        const otherHolders = sortedHolders.slice(5);
        let otherBalance;

        try {
          otherBalance = otherHolders.reduce(
            (sum, holder) => sum + BigInt(holder.balance || 0),
            BigInt(0)
          );
        } catch (err) {
          console.error('Error calculating other holders balance:', err);
          otherBalance = BigInt(0);
        }

        topHolders.push({
          holderAddress: 'その他',
          balance: otherBalance.toString(),
        });
      }

      // 合計残高を計算
      let totalBalance;

      try {
        totalBalance = sortedHolders.reduce(
          (sum, holder) => sum + BigInt(holder.balance || 0),
          BigInt(0)
        );
      } catch (err) {
        console.error('Error calculating total balance:', err);
        totalBalance = BigInt(1); // ゼロ除算を防ぐため1にする
      }

      // パーセント計算して返す - BigInt から安全に変換
      return topHolders.map(holder => {
        let balance;
        let percent = 0;

        try {
          balance = BigInt(holder.balance || 0);

          // 割合の計算
          if (totalBalance > 0) {
            // (balance * 10000) / totalBalance を計算し、Number に変換して 100 で割る
            const rawPercent = Number((balance * BigInt(10000)) / totalBalance);
            percent = rawPercent / 100;
          }
        } catch (err) {
          console.error('Error calculating holder percentage:', err);
          balance = BigInt(0);
          percent = 0;
        }

        return {
          address: holder.holderAddress,
          value: balance.toString(), // 値を文字列として保存
          percent: percent,
          displayPercent: `${percent.toFixed(2)}%`,
          displayBalance: formatTokenAmount(balance.toString(), token?.decimals || 18),
        };
      });
    } catch (err) {
      console.error('Error in getTopHolders:', err);
      return [];
    }
  };

  // トークン量を表示用にフォーマット
  const formatTokenAmount = (amount, decimals) => {
    try {
      // decimalsが未定義の場合はデフォルト値を使用
      const dec = decimals || 18;

      // 入力値が文字列でない場合は変換
      const amountStr = String(amount || '0');

      // BigInt を使用した安全な変換
      const bigAmount = BigInt(amountStr);
      const divisor = BigInt(10) ** BigInt(dec);

      // 整数部分と小数部分を計算
      const integerPart = (bigAmount / divisor).toString();

      // 小数点以下の計算
      const remainder = bigAmount % divisor;
      const decimalsStr = remainder.toString().padStart(dec, '0');

      // 小数点以下を切り詰めて表示 (最大2桁)
      const formattedDecimals = decimalsStr.substring(0, 2);

      // 結果を組み立て
      const valueStr = `${integerPart}.${formattedDecimals}`;

      // 数値として解析 (安全な範囲内のみ)
      const numValue = parseFloat(valueStr);

      // 大きな数値の場合は省略形式で表示
      if (numValue >= 1_000_000) {
        return `${(numValue / 1_000_000).toFixed(2)}M`;
      } else if (numValue >= 1_000) {
        return `${(numValue / 1_000).toFixed(2)}K`;
      } else {
        return numValue.toFixed(2);
      }
    } catch (error) {
      console.error('Token amount formatting error:', error);
      return amount?.toString() || '0';
    }
  };

  // // 総発行量をフォーマット
  // const formatTotalSupply = () => {
  //   if (!token) return '0';

  //   try {
  //     // decimalsが未定義の場合はデフォルト値を使用
  //     const decimals = token.decimals || 18;

  //     const totalSupply = BigInt(token.totalSupply || 0);
  //     const divisor = BigInt(10) ** BigInt(decimals);
  //     const formattedValue = (totalSupply / divisor).toString();

  //     // カンマ区切りで表示
  //     return parseInt(formattedValue || 0).toLocaleString();
  //   } catch (error) {
  //     console.error('Total supply formatting error:', error);
  //     return (token.totalSupply || 0).toString();
  //   }
  // };

  // アドレスを短縮表示
  const shortenAddress = (addr, length = 6) => {
    if (!addr) return '';
    if (addr === 'その他') return addr;

    // アドレスがオブジェクトの場合、hashプロパティを使用
    if (typeof addr === 'object' && addr !== null) {
      // hashプロパティがある場合はそれを使う
      if (addr.hash) {
        addr = addr.hash;
      } else {
        // 適切なプロパティが見つからない場合は文字列変換を試みる
        addr = String(addr);
      }
    } else if (typeof addr !== 'string') {
      // 文字列でなければ変換
      addr = String(addr);
    }

    return `${addr.substring(0, length)}...${addr.substring(addr.length - 4)}`;
  };

  const exportAllHolderCSV = () => {
    const headers = 'Number,Address,Balance\n';
    const rows = tokenHolders
      .sort((a, b) => {
        try {
          const balanceA = BigInt(a.balance || 0);
          const balanceB = BigInt(b.balance || 0);

          if (balanceA > balanceB) return -1;
          if (balanceA < balanceB) return 1;
          return 0;
        } catch (err) {
          return 0;
        }
      })
      .map((holder, index) => {
        return `${index + 1},${holder.holderAddress.hash},${holder.balance}`;
      })
      .join('\n');

    const csvData = headers + rows;
    downloadCSV(csvData, `${token.name}_all_holders.csv`);
  };

  // Helper function to download CSV data
  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">
            エラーが発生しました
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">{error}</p>
          <Link
            to="/tokens"
            className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            コイン一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            コインが見つかりません
          </h1>
          <Link
            to="/tokens"
            className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            コイン一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* ヘッダー部分 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <Link
              to="/tokens"
              className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              コイン一覧に戻る
            </Link>
            <div className="flex items-center">
              {/* トークンイメージを表示 */}
              {token.img ? (
                <img
                  src={token.img}
                  alt={token.name}
                  className="w-12 h-12 rounded-full object-cover mr-3"
                  onError={e => {
                    // 画像読み込みエラー時にフォールバック表示
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}

              {/* フォールバックアイコン */}
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-full ${getTokenColor()} text-gray-700 dark:text-gray-200 font-bold mr-3 ${
                  token.img ? 'hidden' : 'flex'
                }`}
              >
                {getTokenInitials()}
              </div>

              <h1 className="text-2xl font-bold flex items-center text-gray-900 dark:text-white">
                {token.name}
                <span className="ml-2 text-gray-500 dark:text-gray-400">({token.symbol})</span>
              </h1>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <span className="text-gray-500 dark:text-gray-400 mr-2">アドレス:</span>
            <div className="flex items-center">
              <a
                href={getExplorerLink(token.grantToken)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-1"
              >
                {shortenAddress(token.grantToken, 8)}
              </a>
              <button
                onClick={() => copyToClipboard(token.grantToken)}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                title="アドレスをコピー"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
              </button>
              {copySuccess && (
                <span className="ml-1 text-xs text-green-600 dark:text-green-400">
                  コピーしました
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <span className="text-gray-500 dark:text-gray-400 mr-2">発行者:</span>
            <Link
              to={`/user/${token.issuerAddress}`}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              {shortenAddress(token.issuerAddress)}
            </Link>
          </div>

          <div className="flex items-center">
            <span className="text-gray-500 dark:text-gray-400 mr-2">yukichi:</span>
            {token.yukichiurl && (
              <Link
                to={`${token.yukichiurl}`}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                {token.yukichiurl}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'overview'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex justify-center items-center">
              <svg
                className="h-5 w-5 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
                <path d="M9 21L9 9" stroke="currentColor" strokeWidth="2" />
              </svg>
              概要
            </div>
          </button>

          <button
            onClick={() => setActiveTab('holders')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'holders'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex justify-center items-center">
              <UserCircleIcon className="h-5 w-5 mr-1" />
              ホルダー情報
            </div>
          </button>

          <button
            onClick={() => setActiveTab('proposals')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'proposals'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex justify-center items-center">
              <DocumentTextIcon className="h-5 w-5 mr-1" />
              関連提案
            </div>
          </button>
        </div>

        {/* タブコンテンツ */}
        <div className="p-6">
          {/* 概要タブ */}
          {activeTab === 'overview' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    コイン情報
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">名前:</span>
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {token.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">シンボル:</span>
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {token.symbol}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">発行日:</span>
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {format(new Date(token.launchedAt), 'yyyy/MM/dd', { locale: ja })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">上場日:</span>
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {format(new Date(token.graduatedAt), 'yyyy/MM/dd', { locale: ja })}
                        </span>
                      </div>

                      {/* SNS情報を表示 - 存在する場合のみ */}
                      {token.twitterUrl && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Twitter:</span>
                          <a
                            href={`${token.twitterUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {token.twitterUrl}
                          </a>
                        </div>
                      )}
                      {token.telegramUrl && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Telegram:</span>
                          <a
                            href={`${token.telegramUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {token.telegramUrl}
                          </a>
                        </div>
                      )}
                      {token.youtubeUrl && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">YouTube:</span>
                          <a
                            href={
                              token.youtubeUrl.startsWith('http')
                                ? token.youtubeUrl
                                : `https://youtube.com/${token.youtubeUrl}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {token.youtubeUrl.includes('youtube.com/')
                              ? token.youtubeUrl.split('youtube.com/')[1]
                              : token.youtubeUrl}
                          </a>
                        </div>
                      )}
                      {token.websiteUrl && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">website:</span>
                          <a
                            href={
                              token.websiteUrl.startsWith('http')
                                ? token.websiteUrl
                                : `https://${token.websiteUrl}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {token.websiteUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    保有分布
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex justify-center">
                    {tokenHolders.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={getTopHolders()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="percent"
                            nameKey="address"
                            label={({ displayPercent }) => displayPercent}
                          >
                            {getTopHolders().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, props) => {
                              return [
                                // `${props.payload.displayBalance || '0'} ${token.symbol || ''} (${props.payload.displayPercent || '0%'})`,
                                // shortenAddress(name)
                                `${shortenAddress(name)} (${props.payload.displayPercent || '0%'})`,
                              ];
                            }}
                          />
                          <Legend
                            layout="vertical"
                            verticalAlign="middle"
                            align="right"
                            formatter={value => shortenAddress(value)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                        現在のホルダー情報は利用できません
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">説明</h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  {token.description ? (
                    <p className="text-gray-800 dark:text-gray-200">{token.description}</p>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">説明はありません</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ホルダータブ */}
          {activeTab === 'holders' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  ホルダー一覧
                </h3>

                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <button
                    onClick={exportAllHolderCSV}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    CSV
                  </button>
                </div>
              </div>

              {tokenHolders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          アドレス
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          保有量
                        </th>
                        {/* <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          割合
                        </th> */}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {tokenHolders
                        .sort((a, b) => {
                          try {
                            const balanceA = BigInt(a.balance || 0);
                            const balanceB = BigInt(b.balance || 0);

                            if (balanceA > balanceB) return -1;
                            if (balanceA < balanceB) return 1;
                            return 0;
                          } catch (err) {
                            return 0;
                          }
                        })
                        .map((holder, index) => {
                          // let percent = 0;
                          // let balance = BigInt(0);

                          // try {
                          //   balance = BigInt(holder.balance || 0);
                          //   // const totalSupply = BigInt(token.totalSupply || 1);
                          //   // percent = Number((balance * BigInt(10000)) / totalSupply) / 100;
                          // } catch (err) {
                          //   console.error('Error calculating holder percentage:', err);
                          // }

                          return (
                            <tr
                              key={holder.holderAddress}
                              className={
                                index % 2 === 0
                                  ? 'bg-white dark:bg-gray-800'
                                  : 'bg-gray-50 dark:bg-gray-700'
                              }
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Link
                                    to={`/user/${holder.holderAddress.hash}`}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-2"
                                  >
                                    {/* {shortenAddress(holder.holderAddress)} */}
                                    {holder.holderAddress.hash}
                                  </Link>
                                  <a
                                    href={getExplorerLink(holder.holderAddress.hash)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                    title="ブロックエクスプローラーで表示"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  </a>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-gray-800 dark:text-gray-200">
                                {holder.balance}
                                {/* {formatTokenAmount(holder.balance, token.decimals)}{' '} */}
                                {/* {token.symbol} */}
                              </td>
                              {/* <td className="px-6 py-4 whitespace-nowrap text-right text-gray-800 dark:text-gray-200">
                                {percent.toFixed(2)}%
                              </td> */}
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    現在のホルダー情報は利用できません
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 提案タブ */}
          {activeTab === 'proposals' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">関連提案</h3>

                <Link
                  to="/proposals/create"
                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                >
                  <svg
                    className="h-4 w-4 mr-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  新しい提案
                </Link>
              </div>

              {tokenProposals.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {tokenProposals.map(proposal => (
                    <ProposalCard key={proposal.id} proposal={proposal} />
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    このトークンに関連する提案はありません
                  </p>
                  <Link
                    to="/proposals/create"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  >
                    <svg
                      className="h-5 w-5 mr-1"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    提案を作成する
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenDetail;
